import React, { useState, useEffect, useCallback } from 'react'
import {
  Plus,
  MagnifyingGlass,
  FilmSlate,
  Eye,
  Clock,
  Trash,
  PencilSimple,
  Export,
  Scroll,
} from '@phosphor-icons/react'
import api from '../services/api'
import { toast } from 'sonner'
import AddProjectModal from '../components/AddProjectModal'
import { useAuth } from '../contexts/AuthContext'
import { Link, useNavigate } from 'react-router-dom'

const STATUS_CONFIG = {
  idea: {
    label: 'Idea',
    color: '#FFD60A',
    bg: 'rgba(255,214,10,0.08)',
    border: 'rgba(255,214,10,0.25)',
  },
  plan: {
    label: 'Plan',
    color: '#5E5CE6',
    bg: 'rgba(94,92,230,0.08)',
    border: 'rgba(94,92,230,0.25)',
  },
  record: {
    label: 'Grabando',
    color: '#D42B1E',
    bg: 'rgba(212,43,30,0.08)',
    border: 'rgba(212,43,30,0.25)',
  },
  edit: {
    label: 'Editando',
    color: '#FF9F0A',
    bg: 'rgba(255,159,10,0.08)',
    border: 'rgba(255,159,10,0.25)',
  },
  analyze: {
    label: 'Análisis',
    color: '#007AFF',
    bg: 'rgba(0,122,255,0.08)',
    border: 'rgba(0,122,255,0.25)',
  },
  publish: {
    label: 'Publicado',
    color: '#32ADE6',
    bg: 'rgba(50,173,230,0.08)',
    border: 'rgba(50,173,230,0.25)',
  },
}

const PLATFORM_COLORS = {
  YouTube: '#FF0000',
  Instagram: '#E1306C',
  TikTok: '#69C9D0',
  LinkedIn: '#0077B5',
}

const FILTER_TABS = [
  { value: '', label: 'Todos' },
  { value: 'idea', label: 'Idea' },
  { value: 'plan', label: 'Plan' },
  { value: 'record', label: 'Grabar' },
  { value: 'edit', label: 'Editar' },
  { value: 'analyze', label: 'Analizar' },
  { value: 'publish', label: 'Publicado' },
]

function StatCard({ label, value, sub, color }) {
  return (
    <div className="card p-4 flex flex-col gap-1">
      <p className="type-caption text-ds-muted">{label}</p>
      <p
        className="text-2xl font-black"
        style={{ fontFamily: 'Barlow Condensed, sans-serif', color: color || '#E8E6E0' }}
      >
        {value}
      </p>
      {sub && (
        <p className="text-[11px] text-ds-muted" style={{ fontFamily: 'Barlow, sans-serif' }}>
          {sub}
        </p>
      )}
    </div>
  )
}

function ProjectCard({ project, onEdit, onDelete, onOpenScript }) {
  const sc = STATUS_CONFIG[project.status] || STATUS_CONFIG.idea

  return (
    <div
      className="card overflow-hidden hover:-translate-y-0.5 transition-transform duration-150 group"
      data-testid={`project-card-${project.id}`}
    >
      {/* Thumbnail */}
      <div className="h-28 bg-ds-elevated relative overflow-hidden">
        {project.thumbnail_url ? (
          <img
            src={project.thumbnail_url}
            alt=""
            className="w-full h-full object-cover opacity-70"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <FilmSlate size={36} className="text-ds-faint" weight="duotone" />
          </div>
        )}
        {/* Platform badge */}
        <div className="absolute top-2 right-2">
          <span
            className="type-caption px-2 py-0.5 bg-[rgba(13,13,13,0.85)]"
            style={{
              color: PLATFORM_COLORS[project.platform] || '#E8E6E0',
              borderRadius: 2,
            }}
          >
            {project.platform}
          </span>
        </div>
        {/* Overlay actions */}
        <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center gap-2">
          <button
            onClick={() => onEdit(project)}
            className="p-2 bg-ds-elevated hover:bg-[#282828] transition-colors"
            style={{ borderRadius: 2 }}
            data-testid={`edit-project-${project.id}`}
          >
            <PencilSimple size={14} color="#E8E6E0" />
          </button>
          <button
            onClick={() => onOpenScript(project.id)}
            className="p-2 bg-ds-elevated hover:bg-[#282828] transition-colors"
            style={{ borderRadius: 2 }}
            title="Ver guión"
            data-testid={`script-project-${project.id}`}
          >
            <Scroll size={14} color="#E8E6E0" />
          </button>
          <button
            onClick={() => onDelete(project.id)}
            className="p-2 bg-[rgba(212,43,30,0.15)] hover:bg-[rgba(212,43,30,0.25)] transition-colors"
            style={{ borderRadius: 2 }}
            data-testid={`delete-project-${project.id}`}
          >
            <Trash size={14} color="#D42B1E" />
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-2">
          <h3
            className="text-ds-text font-semibold text-sm leading-tight line-clamp-2 flex-1"
            style={{ fontFamily: 'Barlow, sans-serif' }}
          >
            {project.title}
          </h3>
          <span
            className="type-caption px-2 py-0.5 shrink-0"
            style={{
              color: sc.color,
              background: sc.bg,
              border: `1px solid ${sc.border}`,
              borderRadius: 2,
            }}
            data-testid={`project-status-${project.id}`}
          >
            {sc.label}
          </span>
        </div>

        {/* Tags */}
        {project.tags?.length > 0 && (
          <div className="flex flex-wrap gap-1 mb-3">
            {project.tags.slice(0, 3).map(tag => (
              <span
                key={tag}
                className="type-caption text-ds-muted bg-ds-elevated px-1.5 py-0.5"
                style={{ borderRadius: 2 }}
              >
                #{tag}
              </span>
            ))}
            {project.tags.length > 3 && (
              <span className="type-caption text-ds-muted">+{project.tags.length - 3}</span>
            )}
          </div>
        )}

        {/* Footer: metrics + script CTA */}
        <div className="flex items-center justify-between gap-2">
          <div
            className="flex items-center gap-3 text-ds-muted"
            style={{ fontSize: 11, fontFamily: 'Barlow, sans-serif' }}
          >
            <span className="flex items-center gap-1">
              <Eye size={11} />
              {project.estimated_views > 0 ? project.estimated_views.toLocaleString() : '—'}
            </span>
            <span className="flex items-center gap-1">
              <Clock size={11} />
              {project.duration_min}m
            </span>
          </div>
          <button
            onClick={() => onOpenScript(project.id)}
            className="btn-ghost-sm flex items-center gap-1.5"
            data-testid={`script-btn-${project.id}`}
          >
            <Scroll size={11} />
            Guión
          </button>
        </div>
      </div>
    </div>
  )
}

export default function DashboardPage() {
  const { user } = useAuth()
  const navigate = useNavigate()
  const [projects, setProjects] = useState([])
  const [stats, setStats] = useState({
    total: 0,
    by_status: {},
    avg_engagement: 0,
    total_views: 0,
  })
  const [loading, setLoading] = useState(true)
  const [search, setSearch] = useState('')
  const [filter, setFilter] = useState('')
  const [modalOpen, setModalOpen] = useState(false)
  const [editProject, setEditProject] = useState(null)

  const fetchData = useCallback(async () => {
    setLoading(true)
    try {
      const params = {}
      if (filter) params.status = filter
      if (search) params.search = search
      const [proj, st] = await Promise.all([api.get('/projects', { params }), api.get('/stats')])
      setProjects(proj.data)
      setStats(st.data)
    } catch (err) {
      toast.error('Error al cargar vídeos')
    } finally {
      setLoading(false)
    }
  }, [filter, search])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  const handleDelete = async id => {
    if (!window.confirm('¿Eliminar este vídeo?')) return
    try {
      await api.delete(`/projects/${id}`)
      toast.success('Vídeo eliminado')
      fetchData()
    } catch {
      toast.error('Error al eliminar')
    }
  }

  const handleEdit = project => {
    setEditProject(project)
    setModalOpen(true)
  }
  const handleAdd = () => {
    setEditProject(null)
    setModalOpen(true)
  }
  const handleProjectSaved = (data, createAndWrite) => {
    fetchData()
    setModalOpen(false)
    setEditProject(null)
    if (createAndWrite && data?.id) {
      navigate('/scripts', { state: { projectId: data.id } })
    }
  }
  const handleOpenScript = projectId => {
    navigate('/scripts', { state: { projectId } })
  }

  const exportProject = async id => {
    try {
      const res = await api.get(`/projects/${id}/export?fmt=script`)
      const blob = new Blob([res.data], { type: 'text/plain' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'script.txt'
      a.click()
      URL.revokeObjectURL(url)
    } catch {
      toast.error('Error al exportar')
    }
  }

  const publishedCount = stats.by_status?.publish || 0
  const inProgressCount =
    (stats.by_status?.record || 0) + (stats.by_status?.edit || 0) + (stats.by_status?.plan || 0)

  return (
    <div className="p-6 lg:p-8 min-h-full">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="type-h2 text-ds-text text-3xl">Hola, {user?.name?.split(' ')[0]}</h1>
          <p className="text-ds-muted text-sm mt-1" style={{ fontFamily: 'Barlow, sans-serif' }}>
            Gestiona tus vídeos
          </p>
        </div>
        <button
          onClick={handleAdd}
          className="btn-accent flex items-center gap-2"
          data-testid="add-project-btn"
        >
          <Plus size={16} weight="bold" />
          Nueva Idea
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-8">
        <StatCard label="Total Vídeos" value={stats.total} />
        <StatCard label="En Progreso" value={inProgressCount} color="#FF9F0A" />
        <StatCard label="Publicados" value={publishedCount} color="#32ADE6" />
        <StatCard label="Engagement Avg" value={`${stats.avg_engagement}%`} color="#007AFF" />
      </div>

      {/* Search + Filter */}
      <div className="flex flex-col sm:flex-row gap-3 mb-6">
        <div className="relative flex-1">
          <MagnifyingGlass
            size={14}
            className="absolute left-3 top-1/2 -translate-y-1/2 text-ds-muted"
          />
          <input
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Buscar vídeos..."
            className="ds-input pl-9"
            data-testid="search-input"
          />
        </div>
        <div className="flex gap-1.5 overflow-x-auto pb-1">
          {FILTER_TABS.map(tab => (
            <button
              key={tab.value}
              onClick={() => setFilter(tab.value)}
              className={`shrink-0 type-btn text-[11px] px-3 py-2 transition-colors ${
                filter === tab.value
                  ? 'bg-ds-red text-white'
                  : 'bg-ds-elevated border border-ds-border-mid text-ds-muted hover:text-ds-text'
              }`}
              style={{ borderRadius: 2 }}
              data-testid={`filter-${tab.value || 'all'}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Projects Grid */}
      {loading ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {[...Array(6)].map((_, i) => (
            <div
              key={i}
              className="bg-ds-surface h-52 animate-pulse border border-ds-border"
              style={{ borderRadius: 2 }}
            />
          ))}
        </div>
      ) : projects.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-20 text-center">
          <FilmSlate size={48} className="text-ds-faint mb-4" weight="duotone" />
          <h3 className="type-h3 text-ds-text text-lg mb-1">
            {search || filter ? 'No se encontraron vídeos' : 'Sin vídeos todavía'}
          </h3>
          <p className="text-ds-muted text-sm mb-6" style={{ fontFamily: 'Barlow, sans-serif' }}>
            {search || filter
              ? 'Prueba con otros filtros'
              : 'Empieza creando tu primera idea de vídeo'}
          </p>
          {!search && !filter && (
            <button onClick={handleAdd} className="btn-accent" data-testid="empty-add-btn">
              Crear primer vídeo
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
          {projects.map(project => (
            <div key={project.id} className="relative group">
              <ProjectCard
                project={project}
                onEdit={handleEdit}
                onDelete={handleDelete}
                onOpenScript={handleOpenScript}
              />
              <button
                onClick={() => exportProject(project.id)}
                className="absolute top-2 left-2 p-1.5 bg-[rgba(13,13,13,0.85)] opacity-0 group-hover:opacity-100 transition-opacity hover:bg-ds-elevated"
                style={{ borderRadius: 2 }}
                title="Exportar script"
                data-testid={`export-project-${project.id}`}
              >
                <Export size={11} color="#E8E6E0" />
              </button>
            </div>
          ))}
        </div>
      )}

      {/* Floating Add Button (mobile) */}
      <button
        onClick={handleAdd}
        className="fixed bottom-20 right-5 md:hidden w-12 h-12 bg-ds-red flex items-center justify-center z-50 hover:bg-ds-red-hover transition-colors"
        style={{ borderRadius: 2 }}
        data-testid="fab-add-btn"
      >
        <Plus size={22} weight="bold" color="white" />
      </button>

      <AddProjectModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false)
          setEditProject(null)
        }}
        onCreated={handleProjectSaved}
        editProject={editProject}
      />
    </div>
  )
}
